import { createSlice } from '@reduxjs/toolkit';

import { RootState } from '..';
import { TQuestionDifficulty } from '../QuestionSlice';
import { ISubTopic } from '../SubTopicSlice';
import { ITopic } from '../TopicSlice';
import { IBaseUser } from '../UserSlice';
import {
  adminGetQuizList,
  adminCreateQuiz,
  adminLoadMoreQuizList,
  adminGetQuizById,
  adminPublishQuiz,
  adminGetQuizListForAUser,
  adminLoadMoreQuizListForAUser,
} from './QuizAdminApi';
import {
  getQuizById,
  getQuizList,
  loadMoreQuizList,
  getQuizHistory,
  getQuizBySubTopicId,
  checkAllPapersBySubTopicId,
  loadMoreQuizHistory,
} from './QuizApi';

export type TQuizScoreStatus = 'Revise' | 'Good' | 'Very Good';

export interface IQuiz {
  _id: string;
  topic: ITopic;
  isPublished: boolean;
  difficulty: TQuestionDifficulty;
  noOfQuestions: number;
  subTopics: Array<{ subTopic: ISubTopic; paper: string }>;
  createdAt: string;
  updatedAt: string;
  createdBy: IBaseUser;
  updatedBy: IBaseUser;
  assessment?: boolean;
  submissionCount?: number;
}

export interface IQuizState {
  status: 'idle' | 'loading' | 'rejected';
  count: number;
  quiz: IQuiz | undefined;
  list: Array<IQuiz>;
  allPapersStatus: 'idle' | 'loading' | 'rejected';
  allPapers: boolean;
}

const initialState: IQuizState = {
  status: 'idle',
  count: 0,
  quiz: undefined,
  list: [],
  allPapersStatus: 'idle',
  allPapers: false,
};

export const QuizeSlice = createSlice({
  name: 'quiz',
  initialState,
  reducers: {
    resetQuiz: (state) => {
      state.quiz = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      // ADMIN QUIZ LIST
      .addCase(adminGetQuizList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminGetQuizList.fulfilled, (state, action) => {
        state.status = 'idle';
        state.count = action.payload.totalDocs;
        state.list = action.payload.docs;
      })
      .addCase(adminGetQuizList.rejected, (state) => {
        state.status = 'rejected';
      })
      // ADMIN QUIZ LIST FOR A USER
      .addCase(adminGetQuizListForAUser.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminGetQuizListForAUser.fulfilled, (state, action) => {
        state.status = 'idle';
        state.count = action.payload.totalDocs;
        state.list = action.payload.docs;
      })
      .addCase(adminGetQuizListForAUser.rejected, (state) => {
        state.status = 'rejected';
      })
      // ADMIN CREATE QUIZ
      .addCase(adminCreateQuiz.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminCreateQuiz.fulfilled, (state, action) => {
        state.status = 'idle';
        state.count += 1;
        const newList = [action.payload, ...state.list];
        state.list = newList;
      })
      .addCase(adminCreateQuiz.rejected, (state) => {
        state.status = 'rejected';
      })
      // ADMIN LOAD MORE
      .addCase(adminLoadMoreQuizList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminLoadMoreQuizList.fulfilled, (state, action) => {
        state.status = 'idle';
        const newList = [...state.list, ...action.payload.docs];
        state.list = newList;
        state.count = action.payload.totalDocs;
      })
      .addCase(adminLoadMoreQuizList.rejected, (state) => {
        state.status = 'rejected';
      })
      // ADMIN LOAD MORE QUIZ LIST FOR A USER
      .addCase(adminLoadMoreQuizListForAUser.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminLoadMoreQuizListForAUser.fulfilled, (state, action) => {
        state.status = 'idle';
        const newList = [...state.list, ...action.payload.docs];
        state.list = newList;
        state.count = action.payload.totalDocs;
      })
      .addCase(adminLoadMoreQuizListForAUser.rejected, (state) => {
        state.status = 'rejected';
      })
      // ADMIN GET BY ID
      .addCase(adminGetQuizById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminGetQuizById.fulfilled, (state, action) => {
        state.status = 'idle';
        state.quiz = action.payload;
      })
      .addCase(adminGetQuizById.rejected, (state) => {
        state.status = 'rejected';
      })
      // ADMIN PUBLISH QUIZ
      .addCase(adminPublishQuiz.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminPublishQuiz.fulfilled, (state, action) => {
        state.status = 'idle';
        state.quiz = action.payload;
      })
      .addCase(adminPublishQuiz.rejected, (state) => {
        state.status = 'rejected';
      })
      // USER QUIZ LIST
      .addCase(getQuizList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getQuizList.fulfilled, (state, action) => {
        state.status = 'idle';
        state.count = action.payload.totalDocs;
        state.list = action.payload.docs;
      })
      .addCase(getQuizList.rejected, (state) => {
        state.status = 'rejected';
      })
      // USER QUIZ HISTORY
      .addCase(getQuizHistory.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getQuizHistory.fulfilled, (state, action) => {
        state.status = 'idle';
        state.count = action.payload.totalDocs;
        state.list = action.payload.docs;
      })
      .addCase(getQuizHistory.rejected, (state) => {
        state.status = 'rejected';
      })
      // USER LOAD MORE QUIZ LIST
      .addCase(loadMoreQuizList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(loadMoreQuizList.fulfilled, (state, action) => {
        state.status = 'idle';
        const newList = [...state.list, ...action.payload.docs];
        state.list = newList;
        state.count = action.payload.totalDocs;
      })
      .addCase(loadMoreQuizList.rejected, (state) => {
        state.status = 'rejected';
      })
      // USER GET BY ID
      .addCase(getQuizById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getQuizById.fulfilled, (state, action) => {
        state.status = 'idle';
        state.quiz = action.payload;
      })
      .addCase(getQuizById.rejected, (state) => {
        state.status = 'rejected';
      })
      // Get quiz by subtopic id
      .addCase(getQuizBySubTopicId.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getQuizBySubTopicId.fulfilled, (state, action) => {
        state.status = 'idle';
        state.quiz = action.payload;
      })
      .addCase(getQuizBySubTopicId.rejected, (state) => {
        state.status = 'rejected';
      })
      // Check all papers availability by subtopic id
      .addCase(checkAllPapersBySubTopicId.pending, (state) => {
        state.allPapersStatus = 'loading';
      })
      .addCase(checkAllPapersBySubTopicId.fulfilled, (state, action) => {
        state.allPapersStatus = 'idle';
        state.allPapers = !!action.payload;
      })
      .addCase(checkAllPapersBySubTopicId.rejected, (state) => {
        state.allPapersStatus = 'rejected';
      })
      // LOAD MORE QUIZ HISTORY USER
      .addCase(loadMoreQuizHistory.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(loadMoreQuizHistory.fulfilled, (state, action) => {
        state.status = 'idle';

        const newList = [...state.list, ...action.payload.docs];

        state.list = newList;
        state.count = action.payload.totalDocs;
      })
      .addCase(loadMoreQuizHistory.rejected, (state) => {
        state.status = 'rejected';
      });
  },
});

export const getQuizState = (state: RootState): RootState['quiz'] => state.quiz;

export const { resetQuiz } = QuizeSlice.actions;

export * from './QuizAdminApi';
export * from './QuizApi';
export default QuizeSlice.reducer;
